/**
 * Настройки отображения.
 *
 * Это предпочтения одного человека, а не проектные данные: плотность таблиц,
 * формат дат и первый день недели у каждого свои и не должны навязываться
 * команде. Поэтому они живут в localStorage, а не в базе — общими их делать
 * нечего, а лишняя таблица ради оформления только добавила бы миграций.
 *
 * Следствие честно называется в интерфейсе: настройки привязаны к браузеру,
 * и на другом устройстве их придётся выбрать заново.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { setActiveDateStyle } from '@/lib/format';
import { useLocalSetting } from '@/lib/use-local-setting';

export type Density = 'comfortable' | 'compact';
export type DateFormat = 'dd.mm.yyyy' | 'iso';
export type WeekStart = 'monday' | 'sunday';

export interface DisplaySettings {
  density: Density;
  dateFormat: DateFormat;
  weekStart: WeekStart;
}

export const DEFAULT_DISPLAY_SETTINGS: DisplaySettings = {
  density: 'comfortable',
  // Российский формат по умолчанию: интерфейс русский, и «24.08.2026»
  // читается здесь быстрее, чем «2026-08-24».
  dateFormat: 'dd.mm.yyyy',
  weekStart: 'monday',
};

interface DisplayContextValue extends DisplaySettings {
  set: <K extends keyof DisplaySettings>(key: K, value: DisplaySettings[K]) => void;
  /** Номер первого дня недели для расчётов: 1 — понедельник, 0 — воскресенье. */
  weekStartIndex: 0 | 1;
}

const DisplayContext = createContext<DisplayContextValue | null>(null);

export function DisplaySettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useLocalSetting<DisplaySettings>(
    'display',
    DEFAULT_DISPLAY_SETTINGS,
  );

  const value = useMemo<DisplayContextValue>(() => {
    // Настройка могла быть записана прежней версией: недостающие поля
    // берутся из значений по умолчанию, а не превращаются в undefined.
    const merged = { ...DEFAULT_DISPLAY_SETTINGS, ...settings };

    // Форматирование дат живёт вне React: сообщаем ему выбранный формат
    // здесь, а не в каждом вызове formatDate.
    setActiveDateStyle(merged.dateFormat);

    return {
      ...merged,
      weekStartIndex: merged.weekStart === 'sunday' ? 0 : 1,
      set: (key, next) => setSettings({ ...merged, [key]: next }),
    };
  }, [settings, setSettings]);

  return <DisplayContext.Provider value={value}>{children}</DisplayContext.Provider>;
}

/**
 * Настройки отображения.
 *
 * Вне провайдера возвращаются значения по умолчанию: компонент, вырванный в
 * тест или в изолированный рендер, не должен падать из-за оформления.
 */
export function useDisplaySettings(): DisplayContextValue {
  const context = useContext(DisplayContext);
  if (context) return context;

  return {
    ...DEFAULT_DISPLAY_SETTINGS,
    weekStartIndex: 1,
    set: () => {},
  };
}
